import { IDate, IDateRange, ITranslatableKey, MultiselectOption, Period, SiteInfo } from '@cbo/shared-library';
import { IPeriodFilter } from '@cbo/shared-library/response/calendar.response';
import dayjs, { Dayjs } from 'dayjs';
import * as yup from 'yup';
import { DateRangeSelectionTypes, DateRangeValueTypes } from '../../models/config';
import { SingleSelectGroup } from '../../components/GlobalFilterBar/types';

export type Map = {
  [key: string]: string;
};

const formatFilterDate = (value: IDate): IDate => ({ date: dayjs(value.date).format('YYYY/MM/DD') });

const formatFilterDateRange = (value: IDateRange): IDateRange => ({
  startDate: dayjs(value.startDate).format('YYYY/MM/DD'),
  endDate: dayjs(value.endDate).format('YYYY/MM/DD'),
});

export const findCalendarFilter = (
  filters: IPeriodFilter[],
  key: string,
  dateRange = true
): IDate | IDateRange | undefined => {
  const filter = filters.find(
    (periodFilter) => (periodFilter.current as ITranslatableKey<Period | string>).key === key
  );
  if (!filter) return undefined;
  return dateRange ? formatFilterDateRange(filter.current as IDateRange) : formatFilterDate(filter.current as IDate);
};

export const getFilterSites = (sitesRecords: Record<string, SiteInfo>, siteIdSiteNames: string) => {
  let sites = Object.keys(sitesRecords);
  if (siteIdSiteNames) {
    const splitSites = siteIdSiteNames.split(',');
    if (splitSites.length > 0) {
      sites = splitSites.map((x) => {
        const fSite = Object.values(sitesRecords).find((site) =>
          site.referenceId ? `${site.referenceId} - ${site.name}` === x : `${site.name} - ${site.name}` === x
        );
        return fSite?.enterpriseUnitId || '';
      });
    }
  }

  return sites;
};

// Function to get filter request data after updating
export const getFormattedFilterRequest = (
  dateRange: string | undefined,
  sites: Record<string, SiteInfo>,
  siteIdSiteName: string
) => {
  const dates =
    dateRange && dateRange !== DateRangeValueTypes.TODAY && dateRange !== DateRangeValueTypes.YESTERDAY
      ? dateRange.split(' to ')
      : [];

  const enterpriseUnitIds = getFilterSites(sites, siteIdSiteName);
  const formattedStartDate = dayjs(dates[0]).format('YYYY-MM-DD');
  const formattedEndDate = dayjs(dates[1]).subtract(1, 'd').format('YYYY-MM-DD');

  const validRequest = dayjs(formattedEndDate).diff(formattedStartDate, 'day') >= 0;
  const request = {
    startDate: formattedStartDate,
    endDate: formattedEndDate,
    siteIds: enterpriseUnitIds,
  };

  return { validRequest, request };
};

// Function for new MUI filters
// TODO Anisha clean up: Replace function above once all report filters are done
export const getMuiFormattedFilterRequest = (
  startDate: string | null,
  endDate: string | null,
  sites: MultiselectOption[]
) => {
  if (!startDate || !endDate || sites.length === 0) {
    return {
      validRequest: false,
      request: {
        startDate: '',
        endDate: '',
        siteIds: [],
      },
    };
  }

  const enterpriseUnitIds = sites.map((site) => site.id ?? '');
  const validRequest = dayjs(endDate).diff(startDate, 'day') >= 0;
  const request = {
    startDate,
    endDate,
    siteIds: enterpriseUnitIds,
  };
  return { validRequest, request };
};

export const getSiteInfo = (sites: MultiselectOption[] | SingleSelectGroup[]): SiteInfo[] =>
  sites.map((site) => ({
    name: site.label,
    referenceId: site.subtext ?? '',
    enterpriseUnitId: site.id ?? '',
  }));

export const isMultiselectOptionMatch = (value: MultiselectOption, option: MultiselectOption) => {
  const valueHasId = value?.id && value.id !== '';
  const optionHasId = option?.id && option.id !== '';

  if (valueHasId && optionHasId) {
    return value.id === option.id;
  }

  return value.label === option.label && value.subtext === option.subtext;
};

export const getOptionLabels = (options: MultiselectOption[]) => options.map((option) => option.label);

export const getOptionSubtext = (options: MultiselectOption[]) => options.map((option) => option.subtext ?? '');

export const siteAndDateSchema = yup.object({
  sites: yup.array().of(yup.object().shape({ label: yup.string(), id: yup.string() })),
  dateRange: yup.object().shape({ startDate: yup.date().required(), endDate: yup.date().required() }),
});

export const siteAndDateComparisonSchema = yup.object({
  sites: yup.array().of(yup.object().shape({ label: yup.string(), id: yup.string() })),
  dateRange: yup.object().shape({ startDate: yup.date().required(), endDate: yup.date().required() }),
  comparedDateRange: yup.object().shape({ startDate: yup.date().required(), endDate: yup.date().required() }),
});

export const mapSchemaValues = (extraFields: string[] = []) => {
  const newValues: Record<string, yup.AnySchema> = {};
  extraFields.forEach((element) => {
    newValues[element] = yup
      .array()
      .of(yup.object().shape({ label: yup.string(), id: yup.string(), subtext: yup.string() }));
  });
  return newValues;
};

export const mapRangeSchema = (rangeFields: string[] = []) => {
  const newValues: Record<string, yup.AnySchema> = {};
  rangeFields.forEach((element) => {
    newValues[element] = yup.object().shape({ min: yup.number(), max: yup.number() });
  });
  return newValues;
};

export const getSchemaResolver = (
  extraFields: string[] = [],
  hasComparedDateRange = false,
  rangeFields: string[] = []
) =>
  yup.object({
    sites: yup.array().of(yup.object().shape({ label: yup.string(), id: yup.string() })),
    dateRange: yup.object().shape({ startDate: yup.date().required(), endDate: yup.date().required() }),
    ...(hasComparedDateRange && {
      comparedDateRange: yup.object().shape({ startDate: yup.date().required(), endDate: yup.date().required() }),
    }),
    ...mapSchemaValues(extraFields),
    ...mapRangeSchema(rangeFields),
  });

export const lastWeek = dayjs().subtract(7, 'day').format('YYYY-MM-DD');
export const lastMonth = dayjs().subtract(1, 'month').format('YYYY-MM-DD');
export const yesterday = dayjs().subtract(1, 'day').format('YYYY-MM-DD');

export const previousYearSameDay = dayjs(yesterday).subtract(1, 'year').format('YYYY-MM-DD');

export const today = dayjs().format('YYYY-MM-DD');

export const handleSelectionType = (startDate: string, endDate: string) => {
  // This is just an approximation to handle setting the x-axis scale of visualizations
  const startDateDayjs = dayjs(startDate);
  const endDateDayjs = dayjs(endDate);

  const dateDiff = endDateDayjs.diff(startDateDayjs, 'day');
  if (dateDiff === 0) {
    return DateRangeSelectionTypes.DAY;
  }
  if (dateDiff > 0 && dateDiff < 7) {
    return DateRangeSelectionTypes.WEEK;
  }
  if (dateDiff >= 7 && dateDiff < 31) {
    return DateRangeSelectionTypes.MONTH;
  }
  return DateRangeSelectionTypes.YEAR;
};

// Helper function to get end date for filters if you are disabling future dates
export const getEndDate = (date: string) => {
  if (dayjs(date).isAfter(dayjs())) {
    return dayjs().format('YYYY-MM-DD');
  }
  return dayjs(date).format('YYYY-MM-DD');
};

/**
 * Calculate the number of days between two dates
 * @param startDate - Start date in string format (YYYY-MM-DD)
 * @param endDate - End date in string format (YYYY-MM-DD)
 * @returns Number of days between the dates, or 0 if dates are invalid
 */
export const calculateDaysDifference = (startDate: string | undefined, endDate: string | undefined): number => {
  if (!startDate || !endDate) return 0;
  return dayjs(endDate).diff(dayjs(startDate), 'day');
};

// Helper function to get date in MM/DD Format
export const getFormatDateMMDD = (date: string) => dayjs(date).format('MM/DD');

// Helper function to get any input date to check between the any start and end dates
export const isDateBetweenStartAndEndDate = (currentDate: Dayjs, startDate: string | Date, endDate: string | Date) =>
  currentDate.isBetween(startDate, endDate, 'd', '[]');
